import { useEffect, useRef, useState } from 'react';
import { Building2, Truck, User } from 'lucide-react';
import { cn } from '../../lib/cn';

const NODES = [
  {
    icon: Building2,
    label: 'Pharmacy',
    title: 'Order arrives in LYN Rx via secure CSV upload from your PMS.',
    body: 'Cold chain, signature requirements, and delivery notes travel with every package.',
  },
  {
    icon: Truck,
    label: 'Driver',
    title: 'Driver scans every package at pickup inside the pharmacy\'s geofence.',
    body: 'GPS-verified pickup, audit-logged pickup, no exceptions.',
  },
  {
    icon: User,
    label: 'Patient',
    title: 'Driver captures POD at the patient\'s address.',
    body: 'Photo, signature, recipient identity, optional note. Geofence-verified delivery. Every record permanent.',
  },
] as const;

export function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight;
      const total = rect.height + viewport * 0.4;
      const scrolled = viewport - rect.top;
      const ratio = Math.max(0, Math.min(1, scrolled / total));
      setProgress(ratio);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section id="how-it-works" className="relative bg-white py-24 md:py-32">
      <div className="container-page">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">How LYN Rx works</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-ink md:text-4xl">
            Three nodes. One unbroken chain.
          </h2>
          <p className="mt-4 text-base text-ink-2">
            Every prescription leaves with verifiable evidence at each handoff — pharmacy, driver, patient.
          </p>
        </div>

        <div ref={sectionRef} className="relative mx-auto mt-20 max-w-3xl">
          <div
            className="absolute left-7 top-2 bottom-2 w-px bg-border md:left-1/2 md:-translate-x-1/2"
            aria-hidden
          />
          <div
            className="absolute left-7 top-2 w-px bg-gradient-to-b from-primary via-primary to-primary/40 md:left-1/2 md:-translate-x-1/2"
            style={{ height: `calc(${progress * 100}% - 4px)` }}
            aria-hidden
          />

          <ol className="space-y-16 md:space-y-24">
            {NODES.map(({ icon: Icon, label, title, body }, index) => {
              const reachedFraction = (index + 0.5) / NODES.length;
              const reached = progress >= reachedFraction;
              const isLeft = index % 2 === 0;
              return (
                <li key={label} className="relative grid grid-cols-[3.5rem_1fr] items-start gap-6 md:grid-cols-2 md:gap-12">
                  <div className={cn('relative flex md:col-span-2 md:items-center', isLeft ? 'md:flex-row' : 'md:flex-row-reverse')}>
                    <div className="md:w-1/2 md:px-8">
                      <div
                        className={cn(
                          'flex h-14 w-14 items-center justify-center rounded-2xl border bg-white shadow-sm transition-colors duration-500',
                          reached ? 'border-primary bg-primary-soft text-primary' : 'border-border text-ink-3',
                        )}
                      >
                        <Icon className="h-6 w-6" strokeWidth={1.75} />
                      </div>
                    </div>
                    <div className="md:w-1/2 md:px-8">
                      <p className="text-xs font-medium uppercase tracking-wider text-ink-3">
                        Step {index + 1} · {label}
                      </p>
                      <h3 className="mt-2 text-lg font-semibold text-ink md:text-xl">{title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-ink-2">{body}</p>
                    </div>
                  </div>

                  <div
                    className={cn(
                      'absolute left-7 top-6 h-3 w-3 -translate-x-1/2 rounded-full border-2 bg-white transition-colors duration-500 md:left-1/2',
                      reached ? 'border-primary' : 'border-border',
                    )}
                    aria-hidden
                  />
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
